import React from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import { GovernanceListingSection } from '../governance/GovernanceListingSection'
import wfStyles from '@/styles/pages/compliance-workflow/ComplianceWorkflow.module.scss'

type Binding = {
  controlKey: string
  title?: string
  soc2Mapping?: string | null
  expectedEvidence?: Array<{ title: string; requirementKey?: string }>
  questions?: Array<{ questionKey: string; localization?: { en?: string } }>
  questionsTotal?: number
  questionsAnswered?: number
  readinessState?: string | null
}

interface Props {
  standardCode: string
  binding: Binding | null
  informationSystemId?: string | null
  onClose?: () => void
}

export default function ControlExecutionPanel({
  standardCode,
  binding,
  informationSystemId,
  onClose,
}: Props) {
  const { t } = useI18n()
  if (!binding) return null

  const evidenceQuery = new URLSearchParams({
    standard: standardCode,
    control: binding.controlKey,
  })
  if (informationSystemId) evidenceQuery.set('system', informationSystemId)

  return (
    <GovernanceListingSection
      title={`${binding.controlKey} — ${binding.title || binding.controlKey}`}
      toolbar={
        onClose ? (
          <Button type="button" size="small" variant="outline" onClick={onClose}>
            {t('common.close', 'Close')}
          </Button>
        ) : null
      }
    >
      {binding.soc2Mapping ? (
        <p className={wfStyles.itemMeta}>
          {t('canonicalLibrary.soc2Mapping', 'SOC 2 mapping')}: {binding.soc2Mapping}
        </p>
      ) : null}
      {binding.expectedEvidence?.length ? (
        <>
          <p className={wfStyles.itemMeta}>{t('canonicalLibrary.expectedEvidence', 'Expected evidence')}:</p>
          <ul className="m-0 pl-4">
            {binding.expectedEvidence.map((e) => (
              <li key={e.requirementKey || e.title} className={wfStyles.itemMeta}>
                {e.title}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {binding.questions?.length ? (
        <p className={wfStyles.itemMeta}>
          {t('complianceExecution.relatedQuestions', 'Related questions')}: {binding.questionsAnswered || 0}/
          {binding.questionsTotal || binding.questions.length}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2 mt-2">
        <Link to={`/compliance-center/evidence?${evidenceQuery.toString()}`}>
          <Button type="button" size="small" color="brand">
            {t('complianceExecution.action.uploadEvidence', 'Upload Evidence')}
          </Button>
        </Link>
        <Link to={`/compliance-center/assessments?standard=${encodeURIComponent(standardCode)}`}>
          <Button type="button" size="small" variant="outline">
            {t('complianceExecution.action.openQuestions', 'Open Questions')}
          </Button>
        </Link>
      </div>
    </GovernanceListingSection>
  )
}
